
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import * as Cesium from 'cesium';
import * as turf from '@turf/turf';
import { BuildingModelAssetsService } from '../../../core/services/building-model-assets.service';
import { CoresService } from '../../../core/services/cores.service';
import { DataFeedNotificationService } from '../../../core/services/data-feed-notification.service';
import { OrganizationUtilService } from '../../../core/services/organization-util.service';
import { ThreeIntegrationService } from '../../../core/services/three-js-building.service';
import { BlenderBuildingService } from '../../../core/services/blender-building.service';
import {
  resolveCampusGeographicAnchor,
  resolveBuildingLocalCenter,
} from '../../../core/utils/building-georef.util';
import { ViewerAlertRecord, ViewerLiveUserData } from '../../../core/interfaces/building-3d-viewer-interface.interface';

export interface CampusBuildingLoadingStatus {
  perimeterId: string;
  name: string;
  status: 'loading' | 'building' | 'done' | 'error';
  currentFloor: number;
  totalFloors: number;
  progressPercent: number;
  screenX: number;
  screenY: number;
  visible: boolean;
  lat?: number;
  lon?: number;
}

@Component({
  selector: 'app-blender-camera',
  standalone: true,
  imports: [],
  templateUrl: './blender-camera.component.html',
  styleUrl: './blender-camera.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlenderCameraComponent implements OnInit, OnChanges, OnDestroy {
  @Input() buildingMapEntity: any;
  @Input() campusMapEntity: any;
  @Input() campusBuildings: any[] = [];
  @Input() lastFloorStatus: Record<string, string> = {};
  @Input() currentFloorData: Record<string, any> = {};
  @Input() floorsDetails: any[] = [];
  @Input() userByPerimeter: any;

  @Output() floorSelected = new EventEmitter<string>();
  @Output() buildingProgressChange = new EventEmitter<void>();

  @ViewChild('glbMapLoadingOverlay') glbMapLoadingOverlay?: ElementRef<HTMLElement>;

  /** Map GLB via Three.js (rotates/zooms with Cesium at building pin). */
  isGlbMapViewActive = false;
  is3dViewAvailable = false;
  is3dBuildingLoading = false;
  isShowAllBuildingsMode = false;
  isInitialGlbFetch = false;
  buildingProgressPercent = 0;

  viewerLiveUserData: ViewerLiveUserData | null = null;
  viewerAlertRecords: ViewerAlertRecord[] = [];

  campusBuildingLoaders = new Map<string, CampusBuildingLoadingStatus>();
  campusBuildingLoadersArray: CampusBuildingLoadingStatus[] = [];

  getBuildingLoadingStatus(perimeterId?: string | number): CampusBuildingLoadingStatus | undefined {
    if (perimeterId == null || perimeterId === '') return undefined;
    return this.campusBuildingLoaders.get(String(perimeterId));
  }

  private glbLoaderResizeObserver?: ResizeObserver;
  private glbLoaderWindowResize?: () => void;
  private cesiumPostRenderRemove?: () => void;
  private destroy$ = new Subject<void>();
  private isDestroyed = false;

  constructor(
    private threeJsBuildingIntegration: ThreeIntegrationService,
    private blenderBuildingService: BlenderBuildingService,
    private buildingModelAssets: BuildingModelAssetsService,
    private coresService: CoresService,
    private organizationUtilService: OrganizationUtilService,
    private dataFeedNotificationService: DataFeedNotificationService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.isDestroyed = false;

    this.threeJsBuildingIntegration.wireframeReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.isDestroyed) {
          this.resyncThreeJsOccupancyVisuals();
        }
      });

    this.threeJsBuildingIntegration.floorClick$
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        const floorId = event?.floorId;
        if (floorId && !this.isDestroyed) {
          this.handleFloorClickFrom3d(floorId, event?.floorNumberHint);
        }
      });

    this.update3dViewAvailability();
    this.prefetchCampusBuildingAssets();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['buildingMapEntity'] && !changes['buildingMapEntity'].firstChange) {
      this.update3dViewAvailability();
    }
    if (changes['campusBuildings'] && this.campusBuildings?.length) {
      this.prefetchCampusBuildingAssets();
    }
  }

  private prefetchCampusBuildingAssets(): void {
    if (!this.campusBuildings?.length) return;
    this.campusBuildings.forEach((building: any) => {
      if (building?.perimeterId) {
        this.buildingModelAssets
          .getAssetBundle(String(building.perimeterId))
          .pipe(takeUntil(this.destroy$))
          .subscribe();
      }
    });
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.unbindGlbLoaderTracking();
    this.stopCampusLoaderPositionTracking();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get buildingAssetResolutionKey(): string | null {
    const id = this.buildingMapEntity?.perimeterId;
    return id != null && id !== '' ? String(id) : null;
  }

  isGlbViewerActive(): boolean {
    return this.isGlbMapViewActive || this.isShowAllBuildingsMode;
  }

  isSingleBuildingGlbActive(building?: any): boolean {
    const target = building ?? this.buildingMapEntity;
    if (this.isShowAllBuildingsMode && target) {
      if (this.blenderBuildingService.hasCampusGlbBuilding(target)) {
        return this.blenderBuildingService.isCampusBuildingGlbActive(target);
      }
      return this.isGlbViewerActive();
    }
    return this.isGlbMapViewActive;
  }

  toggleSingleBuilding3dViewer(building?: any, _event?: Event): void {
    const target = building ?? this.buildingMapEntity;
    if (this.isShowAllBuildingsMode && target && this.blenderBuildingService.hasCampusGlbBuilding(target)) {
      const toggled = this.blenderBuildingService.toggleCampusBuildingGlb(target);
      if (toggled) {
        this.cdr.detectChanges();
        return;
      }
    }
    this.toggle3dViewer(_event);
  }

  isGlbMapOverlayActive(): boolean {
    return this.threeJsBuildingIntegration.isGlbMapOverlayActive();
  }

  exitIfActive(): void {
    if (this.isGlbViewerActive()) {
      this.exitGlbViewer();
    }
  }

  update3dViewAvailability(): void {
    const key = this.buildingAssetResolutionKey;
    if (!key) {
      this.is3dViewAvailable = false;
      this.cdr.markForCheck();
      return;
    }

    this.buildingModelAssets.getAssetBundle(key).subscribe({
      next: (bundle: any) => {
        this.is3dViewAvailable = !!bundle?.glbUrl;
        this.cdr.markForCheck();
      },
      error: () => {
        this.is3dViewAvailable = false;
        this.cdr.markForCheck();
      },
    });
  }

  syncAlertHighlights(): void {
    this.sync3dAlertHighlightsForCurrentBuilding();
  }

  buildLiveUserDataFromApi(data: any[], currentFloorData: Record<string, any>): void {
    this.buildViewerLiveUserData(data, currentFloorData);
  }

  buildLiveUserDataFromPlayback(dict: Record<string, any>): void {
    this.buildViewerLiveUserDataFromPlayback(dict);
  }

  toggle3dViewer(_event?: Event): void {
    if (this.isGlbViewerActive()) {
      this.exitGlbViewer();
      return;
    }

    this.setGlbMapLoading(true);
    this.prepareMapBackgroundForGlbView();
    this.cdr.detectChanges();
    void this.activateGlbMapView(false).finally(() => {
      this.setGlbMapLoading(false);
    });
  }

  showAllBuildings3D(): void {
    if (!this.campusBuildings || this.campusBuildings.length === 0) {
      return;
    }

    if (this.isGlbViewerActive()) {
      this.exitGlbViewer();
      return;
    }

    this.setGlbMapLoading(true);
    this.isShowAllBuildingsMode = true;
    this.isInitialGlbFetch = true;
    this.initCampusBuildingLoaders();
    this.prepareMapBackgroundForGlbView();
    this.cdr.detectChanges();
    void this.activateGlbMapView(true).finally(() => {
      this.isInitialGlbFetch = false;
      this.setGlbMapLoading(false);
    });
  }

  private get viewer(): Cesium.Viewer | undefined {
    return this.coresService.getViewer();
  }

  private get camera(): Cesium.Camera | undefined {
    return this.viewer?.camera;
  }

  private setGlbMapLoading(loading: boolean): void {
    this.is3dBuildingLoading = loading;
    if (loading) {
      this.cdr.detectChanges();
      requestAnimationFrame(() => this.bindGlbLoaderTracking());
    } else {
      this.unbindGlbLoaderTracking();
      this.cdr.markForCheck();
    }
  }

  /** Size/position loader from visible map area (between open panels), not hard-coded insets. */
  private syncGlbLoaderToMap(): void {
    const overlay = this.glbMapLoadingOverlay?.nativeElement;
    if (!overlay) {
      return;
    }

    let left = 0;
    let top = 0;
    let right = window.innerWidth;
    let bottom = window.innerHeight;

    const topBar = document.getElementById('arclive-session-options-bar');
    const timeline = document.getElementById('arcLiveTimeline');
    const rightPanel = document.querySelector('app-building-camera .right-panel') as HTMLElement | null;
    const floorPanel = document.querySelector('app-floor-visualizer .left-panel') as HTMLElement | null;
    const monitorTab = document.querySelector('.main-tab') as HTMLElement | null;

    if (topBar) {
      top = Math.max(top, topBar.getBoundingClientRect().bottom);
    }
    if (timeline) {
      bottom = Math.min(bottom, timeline.getBoundingClientRect().top);
    }
    if (floorPanel) {
      left = Math.max(left, floorPanel.getBoundingClientRect().right);
    }
    if (monitorTab && monitorTab.offsetParent !== null) {
      left = Math.max(left, monitorTab.getBoundingClientRect().right);
    }
    if (rightPanel) {
      right = Math.min(right, rightPanel.getBoundingClientRect().left);
    }

    const width = Math.max(0, right - left);
    const height = Math.max(0, bottom - top);
    overlay.style.top = `${top}px`;
    overlay.style.left = `${left}px`;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;
  }

  private bindGlbLoaderTracking(): void {
    this.unbindGlbLoaderTracking();
    this.syncGlbLoaderToMap();
    this.glbLoaderWindowResize = () => this.syncGlbLoaderToMap();
    window.addEventListener('resize', this.glbLoaderWindowResize);

    const map = document.getElementById('cesiumContainer');
    if (map && typeof ResizeObserver !== 'undefined') {
      this.glbLoaderResizeObserver = new ResizeObserver(() => this.syncGlbLoaderToMap());
      this.glbLoaderResizeObserver.observe(map);
    }
  }

  private unbindGlbLoaderTracking(): void {
    if (this.glbLoaderWindowResize) {
      window.removeEventListener('resize', this.glbLoaderWindowResize);
      this.glbLoaderWindowResize = undefined;
    }
    this.glbLoaderResizeObserver?.disconnect();
    this.glbLoaderResizeObserver = undefined;
  }

  private exitGlbViewer(): void {
    this.stopCampusLoaderPositionTracking();
    this.campusBuildingLoaders.clear();
    this.campusBuildingLoadersArray = [];
    this.isShowAllBuildingsMode = false;
    this.isGlbMapViewActive = false;
    this.isInitialGlbFetch = false;
    this.setGlbMapLoading(false);
    this.threeJsBuildingIntegration.hideGlbMapOverlay();
    this.showCesiumBuilding();
    this.cdr.detectChanges();
  }

  private initCampusBuildingLoaders(): void {
    this.campusBuildingLoaders.clear();
    this.buildingProgressPercent = 0;
    this.campusBuildings.forEach((b: any) => {
      if (!b?.perimeterId) return;
      const id = String(b.perimeterId);
      const name = b.name || b.perimeterName || b.buildingName || `Building ${id}`;
      const lat = b.lat ?? b.latitude ?? b.centerLat ?? b.entityLocation?.latitude;
      const lon = b.lon ?? b.longitude ?? b.centerLon ?? b.entityLocation?.longitude;
      this.campusBuildingLoaders.set(id, {
        perimeterId: id,
        name,
        status: 'loading',
        currentFloor: 0,
        totalFloors: 1,
        progressPercent: 0,
        screenX: 0,
        screenY: 0,
        visible: true,
        lat: lat != null ? Number(lat) : undefined,
        lon: lon != null ? Number(lon) : undefined,
      });
    });
    this.updateCampusBuildingLoadersArray();
    this.startCampusLoaderPositionTracking();
  }

  private updateBuildingLoaderStatus(
    perimeterId: string,
    status: 'loading' | 'building' | 'done' | 'error',
    currentFloor: number,
    totalFloors: number,
    percent: number
  ): void {
    const item = this.campusBuildingLoaders.get(String(perimeterId));
    if (item) {
      item.status = status;
      item.currentFloor = currentFloor;
      item.totalFloors = totalFloors;
      item.progressPercent = Math.max(item.progressPercent || 0, percent);
      if (status === 'done') {
        item.progressPercent = 100;
        setTimeout(() => {
          item.visible = false;
          this.updateCampusBuildingLoadersArray();
        }, 500);
      }
      this.calculateOverallBuildingProgress();
      this.updateCampusBuildingLoadersArray();
      this.updateCampusBuildingLoaderPositions();
      this.buildingProgressChange.emit();
      this.cdr.markForCheck();
    }
  }

  private calculateOverallBuildingProgress(): void {
    if (!this.campusBuildingLoaders.size) {
      return;
    }
    let sum = 0;
    this.campusBuildingLoaders.forEach((item) => {
      sum += item.progressPercent || 0;
    });
    const avg = Math.min(100, Math.round(sum / this.campusBuildingLoaders.size));
    this.buildingProgressPercent = Math.max(this.buildingProgressPercent || 0, avg);
  }

  private updateCampusBuildingLoadersArray(): void {
    this.campusBuildingLoadersArray = Array.from(this.campusBuildingLoaders.values());
  }

  private startCampusLoaderPositionTracking(): void {
    this.stopCampusLoaderPositionTracking();
    const viewer = this.viewer;
    if (viewer) {
      this.cesiumPostRenderRemove = viewer.scene.postRender.addEventListener(() => {
        this.updateCampusBuildingLoaderPositions();
      });
      this.updateCampusBuildingLoaderPositions();
    }
  }

  private stopCampusLoaderPositionTracking(): void {
    if (this.cesiumPostRenderRemove) {
      this.cesiumPostRenderRemove();
      this.cesiumPostRenderRemove = undefined;
    }
  }

  private updateCampusBuildingLoaderPositions(): void {
    const viewer = this.viewer;
    if (!viewer || !this.campusBuildingLoaders.size) return;

    this.campusBuildingLoaders.forEach((loader) => {
      if (!loader.visible || loader.status === 'done') return;

      let lat = loader.lat;
      let lon = loader.lon;

      if (lat == null || lon == null) {
        const b = this.campusBuildings?.find((cb: any) => String(cb.perimeterId) === loader.perimeterId);
        if (b) {
          lat = b.lat ?? b.latitude ?? b.entityLocation?.latitude;
          lon = b.lon ?? b.longitude ?? b.entityLocation?.longitude;
          if (lat != null && lon != null) {
            loader.lat = Number(lat);
            loader.lon = Number(lon);
          }
        }
      }

      if (lat != null && lon != null) {
        const cartesian = Cesium.Cartesian3.fromDegrees(Number(lon), Number(lat), 15);
        const windowPos = Cesium.SceneTransforms.worldToWindowCoordinates(viewer.scene, cartesian);
        if (windowPos) {
          loader.screenX = windowPos.x;
          loader.screenY = windowPos.y;
        }
      }
    });

    this.updateCampusBuildingLoadersArray();
    this.cdr.markForCheck();
  }

  private showCesiumBuilding(): void {
    if (this.campusMapEntity?.type === 'CampusMapEntity' && this.campusBuildings?.length > 0) {
      this.showAllCampusBuildingsInCesium();
      return;
    }

    if (this.buildingMapEntity?.cesiumEntity) {
      this.buildingMapEntity.cesiumEntity.show = true;
    }
    this.threeJsBuildingIntegration.clearAlertFloorHighlights();
    if (this.threeJsBuildingIntegration.isActive()) {
      this.threeJsBuildingIntegration.setOverlayVisible(true);
      this.resyncThreeJsOccupancyVisuals();
    } else if (this.buildingMapEntity) {
      void this.threeJsBuildingIntegration.openBuilding(this.buildingMapEntity).then(() => {
        this.resyncThreeJsOccupancyVisuals();
      });
    }
    this.viewer?.scene.requestRender();
  }

  /**
   * Re-apply room occupancy colors after GLB → 3JS toggle.
   * parseUsers only updates when status changes; rebuilt/hidden meshes need a forced resync.
   */
  private resyncThreeJsOccupancyVisuals(): void {
    if (!this.threeJsBuildingIntegration.isActive()) {
      return;
    }
    Object.entries(this.lastFloorStatus).forEach(([perimeterId, status]) => {
      if (!status || status === 'initial') {
        return;
      }
      const perimeter = this.organizationUtilService.data.myPerimeters?.[perimeterId];
      if (!perimeter) {
        return;
      }
      const meshKey = String(perimeter.arcAngelId ?? perimeter.id ?? perimeterId);
      if (!ThreeIntegrationService.roomMeshMap.has(meshKey)) {
        return;
      }
      if (status === 'gray') {
        perimeter.resetSafetyStatusByStats?.(perimeter);
        this.threeJsBuildingIntegration.updatePerimeterStatusVisual(perimeter, 'gray');
        return;
      }
      perimeter.setSafetyStatusByStats?.(status);
      this.threeJsBuildingIntegration.updatePerimeterStatusVisual(
        perimeter,
        status as 'safe' | 'unsafe' | 'unknown'
      );
    });
  }

  /** Map GLB on Three.js overlay — same camera sync as wireframe (not Babylon/Cesium Entity). */
  private async activateGlbMapView(campusMode: boolean): Promise<void> {
    try {
      const viewer = this.viewer;
      const placementAnchor = this.getGlbMapPlacementAnchor(viewer);
      const wasWireframeActive = this.threeJsBuildingIntegration.isActive();
      if (!placementAnchor) {
        throw new Error('No GLB map placement anchor');
      }

      if (!this.threeJsBuildingIntegration.prepareGlbMapOverlayContext(placementAnchor, { forceAnchor: false })) {
        throw new Error('Could not prepare GLB map overlay');
      }
      this.threeJsBuildingIntegration.setOverlayVisible(true);

      if (campusMode && this.campusBuildings?.length) {
        this.isShowAllBuildingsMode = true;
        this.initCampusBuildingLoaders();

        const entries = await Promise.all(
          this.campusBuildings
            .filter((b: any) => b?.perimeterId)
            .map(async (building: any) => {
              const bundle = await firstValueFrom(
                this.buildingModelAssets.getAssetBundle(String(building.perimeterId))
              );
              return { glbUrl: bundle?.glbUrl ?? '', building };
            })
        );
        const withGlb = entries.filter((e) => !!e.glbUrl);
        if (!withGlb.length) {
          throw new Error('No GLB URLs for campus buildings');
        }
        const ok = await this.blenderBuildingService.showCampusGlbMapOverlay(
          withGlb,
          (perimeterId, status, currentFloor, totalFloors, percent) => {
            this.updateBuildingLoaderStatus(perimeterId, status, currentFloor, totalFloors, percent);
            if (status === 'building') {
              this.isInitialGlbFetch = false;
              this.cdr.markForCheck();
            }
          }
        );
        if (ok) {
          this.threeJsBuildingIntegration.setLightingMode(true);
        } else {
          throw new Error('Campus GLB map overlay failed');
        }
      } else {
        const perimeterId = String(this.buildingMapEntity?.perimeterId ?? '');
        const bundle = await firstValueFrom(this.buildingModelAssets.getAssetBundle(perimeterId));
        if (!bundle?.glbUrl || !this.buildingMapEntity) {
          throw new Error(`No GLB URL for ${perimeterId}`);
        }
        const ok = await this.threeJsBuildingIntegration.showGlbMapOverlay(
          bundle.glbUrl,
          this.buildingMapEntity
        );
        if (!ok) {
          throw new Error('GLB map overlay failed');
        }
      }

      this.isGlbMapViewActive = true;
      if (!wasWireframeActive) {
        this.applyStandardMapCameraForGlbView();
      } else {
        this.threeJsBuildingIntegration.renderGlbMapOverlayFrame();
        this.viewer?.scene.requestRender();
      }
    } catch (err) {
      console.error('activateGlbMapView failed:', err);
      this.isGlbMapViewActive = false;
      this.isShowAllBuildingsMode = false;
      this.threeJsBuildingIntegration.hideGlbMapOverlay();
      this.showCesiumBuilding();
    }
    this.cdr.detectChanges();
  }

  /** Building pin / footprint center — same ENU origin as wireframe placement. */
  private getGlbMapPlacementAnchor(viewer?: Cesium.Viewer | null): { lat: number; lon: number } | null {
    const existing = this.threeJsBuildingIntegration.getBuildingGeographicCenter();
    if (existing) {
      return existing;
    }

    const viewerRef = viewer ?? this.viewer;
    return (
      resolveBuildingLocalCenter(this.buildingMapEntity) ??
      resolveCampusGeographicAnchor(this.campusBuildings, viewerRef)
    );
  }

  /** initViewPos — camera look-at only (not building ENU origin). */
  private getGlbMapViewAnchor(viewer?: Cesium.Viewer | null): { lat: number; lon: number } | null {
    const candidates = [
      this.buildingMapEntity,
      this.campusMapEntity,
      ...(this.campusBuildings ?? []),
    ];

    for (const target of candidates) {
      if (target?.initViewPosLon != null && target?.initViewPosLat != null) {
        return { lat: target.initViewPosLat, lon: target.initViewPosLon };
      }
    }

    const viewerRef = viewer ?? this.viewer;
    return (
      resolveBuildingLocalCenter(this.buildingMapEntity) ??
      resolveCampusGeographicAnchor(this.campusBuildings, viewerRef)
    );
  }

  /** Same camera distance/pitch as the Three.js wireframe init view (initViewRange ~300m). */
  private applyStandardMapCameraForGlbView(): void {
    if (!this.viewer || !this.camera || !this.buildingMapEntity) {
      return;
    }

    const viewTarget = this.buildingMapEntity;
    const mapAnchor =
      viewTarget.initViewPosLon != null && viewTarget.initViewPosLat != null
        ? { lat: viewTarget.initViewPosLat, lon: viewTarget.initViewPosLon }
        : this.getGlbMapViewAnchor(this.viewer);

    if (!mapAnchor) {
      return;
    }

    this.threeJsBuildingIntegration.setGlbMapGeographicCenter(mapAnchor);

    const center = Cesium.Cartesian3.fromDegrees(mapAnchor.lon, mapAnchor.lat, 10);
    const transform = Cesium.Transforms.eastNorthUpToFixedFrame(center);

    this.viewer.camera.cancelFlight();
    this.camera.lookAtTransform(
      transform,
      new Cesium.HeadingPitchRange(
        viewTarget.initViewHeading ?? 0,
        viewTarget.initViewPitch ?? -0.4,
        viewTarget.initViewRange ?? 300
      )
    );

    this.threeJsBuildingIntegration.renderGlbMapOverlayFrame();
    this.viewer.scene.requestRender();
  }

  private prepareMapBackgroundForGlbView(): void {
    if (this.campusMapEntity?.type === 'CampusMapEntity' && this.campusBuildings?.length > 0) {
      this.campusBuildings.forEach((building: any) => {
        if (building?.cesiumEntity) {
          building.cesiumEntity.show = false;
        }
      });
    } else if (this.buildingMapEntity?.cesiumEntity) {
      this.buildingMapEntity.cesiumEntity.show = false;
    }

    this.threeJsBuildingIntegration.hideWireframeForGlbOverlay();
    this.threeJsBuildingIntegration.setOverlayVisible(true);
    this.viewer?.scene.requestRender();
  }

  private showAllCampusBuildingsInCesium(): void {
    this.campusBuildings.forEach((building: any) => {
      if (building?.cesiumEntity) {
        building.cesiumEntity.show = true;
      }
    });

    const wireframeIntact =
      this.threeJsBuildingIntegration.isActive() &&
      ThreeIntegrationService.roomMeshMap.size > 0;

    if (!wireframeIntact) {
      const buildingsMap = this.campusBuildings.reduce((acc: Record<string, any>, building: any) => {
        const key = building?.id ?? building?.perimeterId;
        if (key) {
          acc[String(key)] = building;
        }
        return acc;
      }, {});
      if (Object.keys(buildingsMap).length > 0) {
        this.threeJsBuildingIntegration.activate3DVisualizationForBuilding(buildingsMap);
      }
    }

    this.threeJsBuildingIntegration.setOverlayVisible(true);
    this.threeJsBuildingIntegration.clearAlertFloorHighlights();
    this.resyncThreeJsOccupancyVisuals();
    this.viewer?.scene.requestRender();
  }

  private sync3dAlertHighlightsForCurrentBuilding(): void {
    const selectedBuildingIds = new Set<string>(
      [
        this.buildingMapEntity?.perimeterId,
        this.buildingMapEntity?.id,
        this.buildingMapEntity?.arcAngelId,
        this.buildingMapEntity?.object?.id,
      ]
        .filter((id) => id !== undefined && id !== null && id !== '')
        .map((id) => String(id))
    );

    if (!selectedBuildingIds.size) {
      this.viewerAlertRecords = [];
      if (this.isGlbMapViewActive || this.isShowAllBuildingsMode) {
        this.threeJsBuildingIntegration.clearAlertFloorHighlights();
      }
      this.cdr.markForCheck();
      return;
    }

    const notifications = (this.dataFeedNotificationService.getNotificationsAsList() || [])
      .filter((noti: any) => this.isAlertNotificationActive(noti));
    const roomIds = new Set<string>();
    const floorIds = new Set<string>();
    const alertRecords: ViewerAlertRecord[] = [];
    const alertRecordKeys = new Set<string>();

    const floorPerimeterMap = new Map<string, any>();
    const roomPerimeterMap = new Map<string, { room: any; floor: any }>();
    const floorToRoomsMap = new Map<string, any[]>();
    this.getBuildingFloorsForAlertMapping().forEach((floor: any) => {
      const floorIdCandidates = [floor?.id, floor?.perimeterId, floor?.arcAngelId]
        .filter((id) => id !== undefined && id !== null && id !== '')
        .map((id) => String(id));
      floorIdCandidates.forEach((id) => floorPerimeterMap.set(id, floor));
      const canonicalFloorId = String(floor?.id || floor?.perimeterId || floor?.arcAngelId || '');
      if (canonicalFloorId) {
        floorToRoomsMap.set(canonicalFloorId, []);
      }

      Object.values(floor?.perimeters || {})
        .filter((p: any) => p?.perimeterType === 'ROOM')
        .forEach((room: any) => {
          const roomIdCandidates = [room?.id, room?.perimeterId, room?.arcAngelId]
            .filter((id) => id !== undefined && id !== null && id !== '')
            .map((id) => String(id));
          roomIdCandidates.forEach((id) => roomPerimeterMap.set(id, { room, floor }));
          const rooms = floorToRoomsMap.get(canonicalFloorId) || [];
          rooms.push(room);
          floorToRoomsMap.set(canonicalFloorId, rooms);
        });
    });

    notifications.forEach((noti: any) => {
      const alertObject = noti?.entity?.object;
      if (!alertObject) {
        return;
      }

      const buildingInfoList = alertObject?.buildingInfo || [];
      const perimeterCandidates = [
        ...(alertObject?.perimeterIds || []),
        ...(alertObject?.sensorPerimeterIds || []),
      ]
        .filter((id: any) => id !== undefined && id !== null && id !== '')
        .map((id: any) => String(id));

      const belongsToSelectedByBuildingInfo = buildingInfoList.some((binding: any) => {
        const buildingIdCandidates = [
          binding?.building?.id,
          binding?.building?.perimeterId,
          binding?.building?.arcAngelId,
        ]
          .filter((id: any) => id !== undefined && id !== null && id !== '')
          .map((id: any) => String(id));
        return buildingIdCandidates.some((id: string) => selectedBuildingIds.has(id));
      });

      const belongsToSelectedByPerimeter = perimeterCandidates.some(
        (pid: string) => floorPerimeterMap.has(pid) || roomPerimeterMap.has(pid)
      );

      if (!belongsToSelectedByBuildingInfo && !belongsToSelectedByPerimeter) {
        return;
      }

      const alertId = String(alertObject?.id || noti?.id || '');

      const pushAlertRecord = (roomId?: string, floorId?: string): void => {
        if (!roomId && !floorId) {
          return;
        }
        const key = `${alertId}:${roomId ?? ''}:${floorId ?? ''}`;
        if (alertRecordKeys.has(key)) {
          return;
        }
        alertRecordKeys.add(key);
        alertRecords.push({
          id: alertId,
          roomId: roomId || undefined,
          floorId: floorId || undefined,
        });
      };

      buildingInfoList.forEach((binding: any) => {
        const buildingIdCandidates = [
          binding?.building?.id,
          binding?.building?.perimeterId,
          binding?.building?.arcAngelId,
        ]
          .filter((id: any) => id !== undefined && id !== null && id !== '')
          .map((id: any) => String(id));
        if (!buildingIdCandidates.some((id: string) => selectedBuildingIds.has(id))) {
          return;
        }
        const roomFromInfo = binding?.room;
        const floorFromInfo = binding?.floor;
        if (roomFromInfo) {
          const roomIdFromInfo = String(
            roomFromInfo?.id ?? roomFromInfo?.perimeterId ?? roomFromInfo?.arcAngelId ?? ''
          );
          const floorIdFromInfo = floorFromInfo
            ? String(floorFromInfo?.id ?? floorFromInfo?.perimeterId ?? floorFromInfo?.arcAngelId ?? '')
            : undefined;
          if (roomIdFromInfo) {
            roomIds.add(roomIdFromInfo);
          }
          if (floorIdFromInfo) {
            floorIds.add(floorIdFromInfo);
          }
        } else if (floorFromInfo) {
          const floorIdFromInfo = String(
            floorFromInfo?.id ?? floorFromInfo?.perimeterId ?? floorFromInfo?.arcAngelId ?? ''
          );
          if (floorIdFromInfo) {
            floorIds.add(floorIdFromInfo);
          }
          pushAlertRecord(undefined, floorIdFromInfo);
        }
      });

      let mappedFloor: any | undefined;
      let mappedRoom: any | undefined;
      perimeterCandidates.forEach((pid: string) => {
        const roomMatch = roomPerimeterMap.get(pid);
        if (roomMatch && !mappedRoom) {
          mappedRoom = roomMatch.room;
          mappedFloor = roomMatch.floor;
          return;
        }
        const floorMatch = floorPerimeterMap.get(pid);
        if (floorMatch && !mappedFloor) {
          mappedFloor = floorMatch;
        }
      });

      // Fallback: resolve room and floor from buildingInfo list
      if (buildingInfoList.length > 0) {
        if (!mappedRoom) {
          const firstWithRoom = buildingInfoList.find((b: any) => b?.room);
          if (firstWithRoom) {
            mappedRoom = firstWithRoom.room;
            if (!mappedFloor && firstWithRoom.floor) {
              mappedFloor = firstWithRoom.floor;
            }
          }
        }
        if (!mappedFloor) {
          const firstWithFloor = buildingInfoList.find((b: any) => b?.floor);
          if (firstWithFloor) {
            mappedFloor = firstWithFloor.floor;
          }
        }
      }

      if (!mappedFloor) {
        return;
      }

      const point = this.extractAlertPointCoordinates(alertObject);
      let roomForIcon: any | undefined = undefined;
      if (point) {
        const floorContainsPoint = this.perimeterContainsCoordinate(mappedFloor, point.lon, point.lat);
        if (floorContainsPoint) {
          const canonicalFloorId = String(
            mappedFloor?.id || mappedFloor?.perimeterId || mappedFloor?.arcAngelId || ''
          );
          const candidateRooms = floorToRoomsMap.get(canonicalFloorId) || [];
          const roomFromCoordinate = candidateRooms.find((room: any) =>
            this.perimeterContainsCoordinate(room, point.lon, point.lat)
          );
          roomForIcon = roomFromCoordinate || undefined;
        } else {
          roomForIcon = undefined;
        }
      }

      const floorIdForHighlight = String(
        mappedFloor?.floorNumber ?? mappedFloor?.id ?? mappedFloor?.perimeterId ?? mappedFloor?.arcAngelId ?? ''
      );
      floorIds.add(floorIdForHighlight);

      const roomForHighlight = roomForIcon || mappedRoom;
      if (roomForHighlight) {
        const roomIdForHighlight = String(
          roomForHighlight?.id ?? roomForHighlight?.perimeterId ?? roomForHighlight?.arcAngelId ?? ''
        );
        if (roomIdForHighlight) {
          roomIds.add(roomIdForHighlight);
          pushAlertRecord(roomIdForHighlight, floorIdForHighlight);
        }
      }
    });

    if (this.isGlbMapViewActive || this.isShowAllBuildingsMode) {
      this.threeJsBuildingIntegration.highlightAlertFloors(Array.from(floorIds));
      this.threeJsBuildingIntegration.highlightAlertRooms(Array.from(roomIds), Array.from(selectedBuildingIds));
    }

    this.viewerAlertRecords = alertRecords;
    this.cdr.markForCheck();
  }

  private getBuildingFloorsForAlertMapping(): any[] {
    if (this.buildingMapEntity?.getFloorPerimeters) {
      return this.buildingMapEntity.getFloorPerimeters() || [];
    }
    if (Array.isArray(this.buildingMapEntity?.floorPerimeters)) {
      return this.buildingMapEntity.floorPerimeters;
    }
    return [];
  }

  private extractAlertPointCoordinates(alertObject: any): { lon: number; lat: number } | null {
    const feature = (alertObject?.AlertFeatures || []).find(
      (f: any) =>
        f?.geometry?.type === 'Point' &&
        Array.isArray(f?.geometry?.coordinates) &&
        f.geometry.coordinates.length >= 2
    );
    if (!feature) {
      return null;
    }
    const [lon, lat] = feature.geometry.coordinates;
    if (typeof lon !== 'number' || typeof lat !== 'number') {
      return null;
    }
    return { lon, lat };
  }

  private isAlertNotificationActive(notification: any): boolean {
    if (!notification?.entity?.object) {
      return false;
    }
    if (notification?.status && notification.status !== 'active') {
      return false;
    }

    const alert = notification.entity.object;
    const now = Date.now();
    const parseDate = (value: any): number | null => {
      if (!value || String(value).toLowerCase() === 'infinity') {
        return null;
      }
      const epoch = Date.parse(value);
      return Number.isNaN(epoch) ? null : epoch;
    };

    const dismissedAt = parseDate(alert.dismissedAt);
    if (dismissedAt !== null && dismissedAt <= now) {
      return false;
    }

    const expiresAt = parseDate(alert.expiresAt);
    if (expiresAt !== null && expiresAt <= now) {
      return false;
    }

    return true;
  }

  private perimeterContainsCoordinate(perimeter: any, lon: number, lat: number): boolean {
    if (!perimeter?.locations || !Array.isArray(perimeter.locations) || !perimeter.locations.length) {
      return false;
    }
    try {
      const polygon = this.makeGeoJsonFromLocation(perimeter.locations);
      const point = turf.point([lon, lat]);
      return turf.booleanPointInPolygon(point, polygon.features[0] as any);
    } catch {
      return false;
    }
  }

  private makeGeoJsonFromLocation(locations: any[]): any {
    const coordinates: number[][] = [];
    for (let i = 0; i < locations.length; i++) {
      coordinates.push([locations[i][1], locations[i][0]]);
    }
    const feature = {
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [coordinates] },
    };
    return { type: 'FeatureCollection', features: [feature] };
  }

  private buildViewerLiveUserData(data: any[], currentFloorData: Record<string, any>): void {
    const records: { userId?: string; floorId?: string; roomId?: string; perimeterId?: string; status?: string }[] = [];
    const byFloor: Record<string, typeof records> = {};
    const byRoom: Record<string, typeof records> = {};

    for (const entry of data) {
      if (entry.type !== 'ROOM') {
        continue;
      }
      const roomId = String(entry.id || '');
      if (!roomId) {
        continue;
      }
      const safeCount = parseInt(entry.safe, 10) || 0;
      const unknownCount = parseInt(entry.unknown, 10) || 0;
      const dangerCount = parseInt(String(entry.danger ?? ''), 10) || 0;
      const totalCount = safeCount + unknownCount + dangerCount;
      if (totalCount <= 0) {
        continue;
      }

      const floorId = entry.parentId ? String(entry.parentId) : undefined;
      const roomRecords: typeof records = [];

      for (let i = 0; i < dangerCount; i++) {
        roomRecords.push({ roomId, floorId, perimeterId: roomId, status: 'danger' });
      }
      for (let i = 0; i < safeCount; i++) {
        roomRecords.push({ roomId, floorId, perimeterId: roomId, status: 'safe' });
      }
      for (let i = 0; i < unknownCount; i++) {
        roomRecords.push({ roomId, floorId, perimeterId: roomId, status: 'unknown' });
      }

      records.push(...roomRecords);
      byRoom[roomId] = (byRoom[roomId] || []).concat(roomRecords);
      if (floorId) {
        byFloor[floorId] = (byFloor[floorId] || []).concat(roomRecords);
      }
    }

    const roomStatusByRoomId: Record<string, { danger: number; safe: number; unknown: number }> = {};
    for (const [roomId, stats] of Object.entries(currentFloorData)) {
      roomStatusByRoomId[roomId] = {
        danger: stats.danger ?? 0,
        safe: stats.safe ?? 0,
        unknown: stats.unknown ?? 0,
      };
    }

    this.viewerLiveUserData = {
      records,
      byFloor,
      byRoom,
      roomStatusByRoomId,
      updatedAt: Date.now(),
    };
    this.cdr.markForCheck();
  }

  private buildViewerLiveUserDataFromPlayback(dict: Record<string, any>): void {
    const records: { userId?: string; floorId?: string; roomId?: string; perimeterId?: string; status?: string }[] = [];
    const byRoom: Record<string, typeof records> = {};
    const byFloor: Record<string, typeof records> = {};
    const roomStatusByRoomId: Record<string, { danger: number; safe: number; unknown: number }> = {};

    const allFloors = this.buildingMapEntity?.getFloorPerimeters?.() ?? this.buildingMapEntity?.floorPerimeters ?? [];
    for (const floor of allFloors) {
      const floorId = String(floor.perimeterId ?? floor.arcAngelId ?? '');
      const roomPerimeters = Object.values(floor.perimeters || {}).filter(
        (p: any) => p.perimeterType === 'ROOM'
      );

      for (const room of roomPerimeters as any[]) {
        const roomId = String(room.arcAngelId ?? room.perimeterId ?? room.id ?? '');
        const numericId = roomId.replace('arc-angel-', '');
        const stats = dict[roomId] ?? dict[numericId];
        if (!stats) continue;

        const dangerCount = parseInt(stats.danger, 10) || 0;
        const safeCount = parseInt(stats.safe, 10) || 0;
        const unknownCount = parseInt(stats.unknown, 10) || 0;
        const totalCount = dangerCount + safeCount + unknownCount;
        if (totalCount <= 0) continue;

        const roomRecords: typeof records = [];
        for (let i = 0; i < dangerCount; i++) roomRecords.push({ roomId, floorId, perimeterId: roomId, status: 'danger' });
        for (let i = 0; i < safeCount; i++) roomRecords.push({ roomId, floorId, perimeterId: roomId, status: 'safe' });
        for (let i = 0; i < unknownCount; i++) roomRecords.push({ roomId, floorId, perimeterId: roomId, status: 'unknown' });

        records.push(...roomRecords);
        byRoom[roomId] = (byRoom[roomId] || []).concat(roomRecords);
        if (floorId) byFloor[floorId] = (byFloor[floorId] || []).concat(roomRecords);

        roomStatusByRoomId[roomId] = { danger: dangerCount, safe: safeCount, unknown: unknownCount };
      }
    }
    this.viewerLiveUserData = { records, byFloor, byRoom, roomStatusByRoomId, updatedAt: Date.now() };
    this.cdr.markForCheck();
  }

  private handleFloorClickFrom3d(floorId: string, floorNumberHint?: number): void {
    const areaEntity = this.threeJsBuildingIntegration.getAreaMapEntity(floorId);
    const lookupIds = new Set<string>([String(floorId).trim()]);
    if (areaEntity) {
      [areaEntity.id, areaEntity.perimeterId, areaEntity.arcAngelId, areaEntity.label, areaEntity.name]
        .filter((id) => id !== undefined && id !== null && id !== '')
        .forEach((id) => lookupIds.add(String(id)));
    }

    let match: { floorEntity: any; index: number } | null = null;
    for (const id of lookupIds) {
      match = this.findFloorEntity(id, floorNumberHint);
      if (match?.floorEntity) {
        break;
      }
    }

    if (!match?.floorEntity) {
      return;
    }

    const emitId = String(
      match.floorEntity.id ??
      match.floorEntity.perimeterId ??
      match.floorEntity.arcAngelId ??
      floorId
    );
    this.floorSelected.emit(emitId);
  }

  private findFloorEntity(floorId: string, floorNumberHint?: number): { floorEntity: any; index: number } | null {
    const visibleFloors = this.floorsDetails?.length ? this.floorsDetails : [];
    const buildingFloors = this.getBuildingFloors();
    const candidates = visibleFloors.length ? visibleFloors : buildingFloors;
    const normalizedClickId = this.normalizeFloorToken(floorId);
    const opaqueId = this.looksLikeOpaqueEntityId(floorId);
    let normalizedClickNumber = '';
    if (floorNumberHint !== undefined && Number.isFinite(floorNumberHint)) {
      normalizedClickNumber = String(floorNumberHint);
    } else if (!opaqueId) {
      normalizedClickNumber = this.extractFloorNumberToken(floorId);
    }

    const foundIndex = candidates.findIndex((floor: any) =>
      this.isMatchingFloor(floor, floorId, normalizedClickId, normalizedClickNumber)
    );

    if (foundIndex === -1) {
      return null;
    }

    const floorEntity = candidates[foundIndex];
    const visibleIndex = visibleFloors.indexOf(floorEntity);

    return {
      floorEntity,
      index: visibleIndex !== -1 ? visibleIndex : foundIndex,
    };
  }

  private looksLikeOpaqueEntityId(id: string): boolean {
    const s = String(id ?? '').trim();
    if (s.length >= 22) {
      return true;
    }
    return /^[0-9a-f]{8}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f-]{4}-[0-9a-f]{12}$/i.test(s);
  }

  private getBuildingFloors(): any[] {
    if (this.buildingMapEntity?.getFloorPerimeters) {
      return this.buildingMapEntity.getFloorPerimeters() || [];
    }
    if (Array.isArray(this.buildingMapEntity?.floorPerimeters)) {
      return this.buildingMapEntity.floorPerimeters;
    }
    return [];
  }

  private isMatchingFloor(
    floor: any,
    rawFloorId: string,
    normalizedClickId: string,
    normalizedClickNumber: string
  ): boolean {
    if (!floor) {
      return false;
    }

    const floorIds = [floor.id, floor.perimeterId, floor.arcAngelId, floor.label, floor.name].filter(Boolean);

    if (floorIds.some((value) => String(value) === String(rawFloorId))) {
      return true;
    }

    if (normalizedClickId && floorIds.some((value) => this.normalizeFloorToken(value) === normalizedClickId)) {
      return true;
    }

    const floorNumber = this.getFloorNumberToken(floor);
    return !!normalizedClickNumber && !!floorNumber && floorNumber === normalizedClickNumber;
  }

  private getFloorNumberToken(floor: any): string {
    const fromMethod = typeof floor?.getFloorNumber === 'function' ? floor.getFloorNumber() : undefined;
    const fromValue = fromMethod ?? floor?.floorNumber ?? floor?.level;
    if (fromValue !== undefined && fromValue !== null && fromValue !== '') {
      return String(Number(fromValue));
    }

    const fromLabel = this.extractFloorNumberToken(floor?.label || floor?.name || '');
    if (fromLabel) {
      return fromLabel;
    }

    return this.extractFloorNumberToken(floor?.id || floor?.perimeterId || floor?.arcAngelId || '');
  }

  private normalizeFloorToken(value: any): string {
    const raw = String(value ?? '').trim().toUpperCase();
    if (!raw) {
      return '';
    }
    const numeric = this.extractFloorNumberToken(raw);
    return numeric || raw;
  }

  private extractFloorNumberToken(value: any): string {
    const match = String(value ?? '').match(/\d+/);
    if (!match) {
      return '';
    }
    return String(Number(match[0]));
  }
}
